import { useEffect, useRef, useState } from "react";
import { getMessages, sendMessage } from "../firebase/services";

const SYSTEM_ICONS = { ticket_open: "🔧", ticket_update: "🔄", payment_confirmed: "✅", default: "ℹ️" };

export default function Chat({ roomId, currentUser, roomLabel = "Conversation", onBack }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!roomId) return;
    const unsub = getMessages(roomId, setMessages);
    return unsub;
  }, [roomId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setText("");
    await sendMessage(roomId, {
      text: trimmed,
      senderId: currentUser.uid,
      senderRole: currentUser.role,
    });
    setSending(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const isOwn = (msg) => msg.senderId === currentUser.uid;
  const isSystem = (msg) => msg.type && msg.type !== "text";

  return (
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.header}>
        {onBack && (
          <button style={styles.backBtn} onClick={onBack} aria-label="Retour">←</button>
        )}
        <div style={{ flex: 1 }}>
          <p style={styles.headerTitle}>{roomLabel}</p>
          <p style={styles.headerSub}>Conversation sécurisée</p>
        </div>
      </div>

      {/* Messages */}
      <div style={styles.messages}>
        {messages.length === 0 && (
          <div style={styles.emptyChat}>
            <span style={{ fontSize: 32 }}>💬</span>
            <p style={{ color: "#7a90a8", fontSize: 13, marginTop: 8 }}>Aucun message. Démarrez la conversation !</p>
          </div>
        )}
        {messages.map((msg) => {
          if (isSystem(msg)) {
            return (
              <div key={msg.id} style={styles.sysBubble}>
                <span style={{ marginRight: 6 }}>{SYSTEM_ICONS[msg.type] || SYSTEM_ICONS.default}</span>
                {msg.text}
              </div>
            );
          }
          const own = isOwn(msg);
          return (
            <div key={msg.id} style={{ display: "flex", justifyContent: own ? "flex-end" : "flex-start", marginBottom: 6 }}>
              <div style={{ maxWidth: "78%" }}>
                <div style={{ ...styles.bubble, ...(own ? styles.bubbleOut : styles.bubbleIn) }}>
                  {msg.text}
                </div>
                <p style={{ ...styles.bubbleTime, textAlign: own ? "right" : "left" }}>
                  {own ? "Vous" : (msg.senderRole === "owner" ? "IAM" : "Étudiant")} · {formatTime(msg.createdAt)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={styles.inputBar}>
        <input
          style={styles.input}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Écrire un message..."
          disabled={sending}
        />
        <button
          style={{ ...styles.sendBtn, opacity: text.trim() ? 1 : 0.4 }}
          onClick={handleSend}
          disabled={!text.trim() || sending}
          aria-label="Envoyer"
        >➤</button>
      </div>
    </div>
  );
}

const styles = {
  wrap: { display: "flex", flexDirection: "column", height: "100%", background: "#f5f8fc", fontFamily: "'Barlow', sans-serif" },
  header: { background: "#1a3a5c", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 },
  backBtn: { background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer", padding: "0 8px 0 0" },
  headerTitle: { fontSize: 14, fontWeight: 700, color: "white", margin: 0 },
  headerSub: { fontSize: 11, color: "#90bde0", margin: 0 },
  messages: { flex: 1, overflowY: "auto", padding: "12px 14px" },
  emptyChat: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", paddingTop: 60 },
  sysBubble: { background: "#e3f1fb", color: "#1a3a5c", borderRadius: 10, padding: "6px 12px", fontSize: 12, textAlign: "center", margin: "6px auto", maxWidth: "80%" },
  bubble: { padding: "9px 13px", borderRadius: 14, fontSize: 14, lineHeight: 1.5 },
  bubbleIn: { background: "white", color: "#1a2a3a", borderBottomLeftRadius: 4, border: "0.5px solid #d0dce8" },
  bubbleOut: { background: "#1a3a5c", color: "white", borderBottomRightRadius: 4 },
  bubbleTime: { fontSize: 10, color: "#7a90a8", margin: "3px 2px 0" },
  inputBar: { display: "flex", gap: 8, padding: "10px 14px", borderTop: "0.5px solid #d0dce8", background: "white" },
  input: { flex: 1, border: "1.5px solid #d0dce8", borderRadius: 22, padding: "10px 14px", fontSize: 14, outline: "none", fontFamily: "inherit", background: "#f5f8fc", color: "#1a2a3a" },
  sendBtn: { width: 42, height: 42, borderRadius: "50%", background: "#1a3a5c", color: "white", border: "none", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
};
